import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { View } from 'react-native';
import {
    ReactFlow, ReactFlowProvider, Background, Controls, MiniMap, Panel,
    useNodesState, useEdgesState, useReactFlow, useNodesInitialized,
    addEdge, reconnectEdge, applyNodeChanges, MarkerType,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { useUnistyles } from 'react-native-unistyles';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Toast from 'react-native-toast-message';
import { Query } from '@/utils/react-query/Queries';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import { supabase } from '@/data/client';
import Unicon from '@/components/General/Unicon';
import {
    savePathStepEdge, deletePathStepEdge, saveCanvasPositions, swapAdjacentSteps, moveStepOntoEdge, detachPathStep,
} from '@/data/paths';
import TriggerNode from './flow/nodes/TriggerNode';
import StepNode from './flow/nodes/StepNode';
import BranchStubNode from './flow/nodes/BranchStubNode';
import AddStepNode from './flow/nodes/AddStepNode';
import PathEdge from './flow/edges/PathEdge';
import StubEdge from './flow/edges/StubEdge';
import { buildGraph } from './flow/buildGraph';
import { validateConnection, validateMove } from './flow/validation';
import { autoLayout } from './flow/layout';
import { resolveBranchLabel } from './flow/labels';
import { parseSourceHandleId, DEFAULT_SOURCE_HANDLE_ID } from './flow/handles';
import { FONT, NODE_WIDTH, STEP_HEIGHT, TASK_STEP_HEIGHT, DEFAULT_EDGE_COLOR, LAYOUT_GAP_Y } from './flow/constants';
import { AUTOMATION_DUE_TYPES } from '@/utils/constants/taskTypes';

const getStepHeight = (step) => step?.stepType === 'TASK' ? TASK_STEP_HEIGHT : STEP_HEIGHT;
const getNodeHeight = (node) => node?.measured?.height || getStepHeight(node?.data?.step);

const nodeTypes = { trigger: TriggerNode, step: StepNode, branchStub: BranchStubNode, addStep: AddStepNode };
const edgeTypes = { pathEdge: PathEdge, stubEdge: StubEdge };

function buildOptimisticEdge(connection) {
    return {
        id: `optimistic-${Date.now()}`,
        source: connection.source,
        sourceHandle: connection.sourceHandle,
        target: connection.target,
        targetHandle: connection.targetHandle,
        type: 'pathEdge',
        style: { stroke: DEFAULT_EDGE_COLOR, strokeWidth: 2 },
        data: { pathEdgeID: null },
        markerEnd: { type: MarkerType.ArrowClosed },
    };
}

function FlowEditorInner({
    path, steps, edges: dbEdges, onEditStep, onAddStep, onUpdateTrigger,
    onEnrollPerson, onEnrollRole, enrolling, onInsertStep,
    onSelectStep, enrollmentStepStatus, enrollmentStepDetails, enrollee, readOnly, outOfScopeBranches,
    triggeringForms, onDuplicateStep,
    triggeringEvents, onConfigureTrigger, triggerOptions,
}) {
    const { theme } = useUnistyles();
    const isDark = theme.scheme === 'dark';
    const queryClient = useQueryClient();
    const { fitView, screenToFlowPosition, getNode } = useReactFlow();
    const nodesInitialized = useNodesInitialized();

    // Lookup data for resolving action target names on step nodes
    const { data: roles } = Query.roles.all({ options: { staleTime: Infinity } });
    const trainingModulesQuery = Query.trainingModules.all({ options: { staleTime: Infinity } });
    const trainingModules = useMemo(
        () => trainingModulesQuery?.data?.pages?.flatMap(p => p.data) || [],
        [trainingModulesQuery?.data?.pages],
    );
    const { data: emailTemplates } = Query.emailTemplates.all({ options: { staleTime: Infinity } });
    const { data: smallGroups } = Query.smallGroups.forDropdown({ options: { staleTime: Infinity } });
    const { data: boards } = Query.boards.all({ options: { staleTime: Infinity } });
    const { data: allPaths } = Query.paths.all({ options: { staleTime: Infinity } });
    const { data: eventInstancesForRegistration } = Query.eventInstances.forRegistration({ options: { staleTime: 1000 * 60 * 5 } });
    const actionLookups = useMemo(() => ({
        roles, trainingModules, emailTemplates, smallGroups, boards,
        paths: allPaths, eventInstances: eventInstancesForRegistration,
    }), [roles, trainingModules, emailTemplates, smallGroups, boards, allPaths, eventInstancesForRegistration]);
    const actionLookupsRef = useRef(actionLookups);
    useEffect(() => { actionLookupsRef.current = actionLookups; }, [actionLookups]);

    const pathIDForTasks = steps?.[0]?.pathID || path?.id || null;
    const pathsTaskQuery = Query.tasks.byDueType({ dueType: AUTOMATION_DUE_TYPES, options: { staleTime: Infinity } });
    const pathsTasks = pathsTaskQuery?.data;
    const { data: pathAssignmentMembers } = useQuery({
        queryKey: ['pathStepAssignmentMembers', pathIDForTasks],
        queryFn: async () => {
            const { data, error } = await supabase.from('TaskAssignmentMembers')
                .select('*, Person:People(id, firstName, lastName, avatarUpdatedAt, avatarStorageKey)')
                .eq('pathID', pathIDForTasks)
                .order('position', { ascending: true });
            if (error) return [];
            return data || [];
        },
        enabled: !!pathIDForTasks,
        staleTime: 1000 * 60 * 5,
    });
    const taskLookups = useMemo(() => {
        const tasks = new Map((pathsTasks || []).map(task => [task.id, task]));
        const membersByTask = new Map();
        (pathAssignmentMembers || []).forEach(member => {
            if (!membersByTask.has(member.taskID)) membersByTask.set(member.taskID, []);
            membersByTask.get(member.taskID).push(member);
        });
        return { tasks, membersByTask };
    }, [pathsTasks, pathAssignmentMembers]);
    const taskLookupsRef = useRef(taskLookups);
    useEffect(() => { taskLookupsRef.current = taskLookups; }, [taskLookups]);

    const [nodes, setNodes, onNodesChange] = useNodesState([]);
    const [edgesState, setEdges, onEdgesChange] = useEdgesState([]);
    const nodesRef = useRef(nodes);
    const autoLayoutRun = useRef(false);
    const reconnectingEdgeID = useRef(null);
    const reconnectSucceeded = useRef(false);
    const [hoveredEdgeID, setHoveredEdgeID] = useState(null);
    const [spliceHint, setSpliceHint] = useState(null);
    const lastDeletedEdgeRef = useRef(null);
    const draggingStepRef = useRef(null);
    const handleStepMenuRef = useRef(null);
    const [layoutSaved, setLayoutSaved] = useState(false);
    const layoutSavedTimer = useRef(null);

    // Briefly show the "Layout Saved" indicator, then fade it out after ~4s.
    const showLayoutSaved = useCallback(() => {
        setLayoutSaved(true);
        if (layoutSavedTimer.current) clearTimeout(layoutSavedTimer.current);
        layoutSavedTimer.current = setTimeout(() => setLayoutSaved(false), 4000);
    }, []);

    useEffect(() => () => {
        if (layoutSavedTimer.current) clearTimeout(layoutSavedTimer.current);
    }, []);

    const displayEdges = useMemo(() => edgesState.map(e => ({
        ...e,
        style: e.id === hoveredEdgeID ? { ...e.style, strokeWidth: 4 } : e.style,
    })), [edgesState, hoveredEdgeID]);

    const invalidStepIDs = useMemo(() => {
        const targets = new Set(edgesState.filter(e => e.type === 'pathEdge').map(e => e.target));
        return new Set(nodes.filter(n => n.type === 'step' && !targets.has(n.id)).map(n => n.id));
    }, [nodes, edgesState]);
    const invalidStepIDsRef = useRef(invalidStepIDs);
    useEffect(() => { invalidStepIDsRef.current = invalidStepIDs; }, [invalidStepIDs]);

    useEffect(() => { nodesRef.current = nodes; }, [nodes]);

    const graphSignature = useMemo(() => JSON.stringify({
        steps: (steps || []).map(s => [s.id, s.stepType, s.name, s.description, s.actionType, s.isEnabled, s.advanceOn, s.config]),
        edges: (dbEdges || []).map(e => [e.id, e.sourceStepID, e.targetStepID, e.branchID, e.branchLabel]),
        outOfScopeBranches: Array.from(outOfScopeBranches || new Set()),
        recurrence: path?.Recurrence?.[0] || null,
        enrollmentStepDetails,
        enrolleeID: enrollee?.id || null,
    }), [steps, dbEdges, outOfScopeBranches, path?.Recurrence, enrollmentStepDetails, enrollee?.id]);

    // Recompute the new step's position from the source node's LIVE position.
    // canvasPosition/basePosition are snapshots from graph-build time and go
    // stale after drags or Tidy Up (saved positions only refetch later).
    const handleAddStepFromNode = useCallback((sourceStepID, branchID, branchLabel, canvasPosition, basePosition) => {
        const sourceNode = getNode(sourceStepID);
        let position = canvasPosition;
        if (sourceNode && canvasPosition && basePosition) {
            position = {
                x: sourceNode.position.x + (canvasPosition.x - basePosition.x),
                y: sourceNode.position.y + (canvasPosition.y - basePosition.y),
            };
        }
        onAddStep?.(sourceStepID, branchID, branchLabel, position);
    }, [getNode, onAddStep]);

    useEffect(() => {
        const g = buildGraph({
            path, steps, edges: dbEdges,
            onEditStep, onAddStep: handleAddStepFromNode, onUpdateTrigger,
            onEnrollPerson, onEnrollRole, enrolling,
            onInsertStep, onDeleteEdge: handleDeleteEdgeFromData,
            onStepMenu: (step, action) => handleStepMenuRef.current?.(step, action),
            enrollmentStepStatus,
            readOnly,
            outOfScopeBranches,
            triggeringForms,
            triggeringEvents,
            onConfigureTrigger,
            triggerOptions,
            actionLookups: actionLookupsRef.current,
            taskLookups: taskLookupsRef.current,
            enrollmentStepDetails,
            enrollee,
        });
        setNodes(prev => {
            const posMap = {};
            prev.forEach(n => { posMap[n.id] = n.position; });
            return g.nodes.map(n => ({ ...n, position: posMap[n.id] ?? n.position }));
        });
        setEdges(g.edges);
    }, [graphSignature, enrollmentStepStatus, readOnly, theme.scheme, outOfScopeBranches, triggeringForms, triggeringEvents]);

    // Patch lookup and enrollment data into existing nodes when queries load,
    // without triggering a full graph rebuild (avoids setNodes loop).
    useEffect(() => {
        setNodes(prev => prev.map(node => {
            if (node.type !== 'step' && node.type !== 'trigger') return node;
            return {
                ...node,
                data: { ...node.data, actionLookups, taskLookups, enrollmentStepDetails, enrollee },
            };
        }));
    }, [actionLookups, taskLookups, enrollmentStepDetails, enrollee]);

    // Auto-layout once on mount when any step lacks a saved position
    useEffect(() => {
        if (!nodesInitialized || autoLayoutRun.current) return;
        const hasMissingPosition = steps?.some(s => s.stepType !== 'TRIGGER' && !s.config?.canvasPosition);
        if (!hasMissingPosition) return;

        const positions = autoLayout(nodes, edgesState);
        const next = nodes.map(n => positions[n.id] ? { ...n, position: positions[n.id] } : n);
        setNodes(next);
        autoLayoutRun.current = true;

        requestAnimationFrame(() => {
            fitView({ padding: 0.3, maxZoom: 1.2, duration: 300 });
            flushPositions(next);
        });
    }, [nodesInitialized]);

    const flushPositions = (nodeList) => {
        if (invalidStepIDsRef.current.size > 0) return;

        const list = nodeList || nodesRef.current;
        if (!list?.length) return;

        const stepPositions = list
            .filter(n => n.type === 'step' || n.type === 'trigger')
            .map(n => {
                const base = n.type === 'step' ? (n.data?.step?.config || {}) : (n.data?.triggerConfig || {});
                return {
                    id: n.id,
                    config: { ...base, canvasPosition: n.position },
                };
            });

        if (!stepPositions.length) return;
        saveCanvasPositionsMutation.mutate({ stepPositions, edgePositions: [] });
    };

    const handleMutationError = (error, fallback) => {
        if (error?.code === '23505') {
            Toast.show({ type: 'error', text1: 'Output already connected', text2: 'That step output already leads somewhere.' });
            return;
        }
        Toast.show({ type: 'error', text1: 'Error', text2: error?.message || fallback });
    };

    const savePathStepEdgeMutation = useMutation({
        mutationFn: savePathStepEdge,
        onSettled: () => queryClient.invalidateQueries({ queryKey: QueryKeys.paths.byId(path?.id) }),
        onError: (e) => handleMutationError(e, 'Something went wrong saving that connection'),
    });

    const deletePathStepEdgeMutation = useMutation({
        mutationFn: deletePathStepEdge,
        onSettled: () => queryClient.invalidateQueries({ queryKey: QueryKeys.paths.byId(path?.id) }),
        onError: (e) => handleMutationError(e, 'Something went wrong deleting that connection'),
    });

    const swapMutation = useMutation({
        mutationFn: swapAdjacentSteps,
        onSettled: () => queryClient.invalidateQueries({ queryKey: QueryKeys.paths.byId(path?.id) }),
        onError: (e) => handleMutationError(e, 'Something went wrong reordering steps'),
    });

    const saveCanvasPositionsMutation = useMutation({
        mutationFn: saveCanvasPositions,
        onSuccess: showLayoutSaved,
        onSettled: () => queryClient.invalidateQueries({ queryKey: QueryKeys.paths.byId(path?.id) }),
        onError: (e) => handleMutationError(e, 'Something went wrong saving positions'),
    });

    const moveStepOntoEdgeMutation = useMutation({
        mutationFn: moveStepOntoEdge,
        onSettled: () => queryClient.invalidateQueries({ queryKey: QueryKeys.paths.byId(path?.id) }),
        onError: (e) => handleMutationError(e, 'Something went wrong moving that step'),
    });

    const detachPathStepMutation = useMutation({
        mutationFn: detachPathStep,
        onSettled: () => queryClient.invalidateQueries({ queryKey: QueryKeys.paths.byId(path?.id) }),
        onError: (e) => handleMutationError(e, 'Something went wrong detaching that step'),
    });

    const handleIsValidConnection = useCallback((connection) => {
        return validateConnection({
            connection,
            nodes,
            edges: edgesState,
            ignoreEdgeID: reconnectingEdgeID.current,
        }).ok;
    }, [nodes, edgesState]);

    const handleConnect = useCallback((connection) => {
        const { ok, reason } = validateConnection({
            connection, nodes, edges: edgesState, ignoreEdgeID: null,
        });
        if (!ok) {
            Toast.show({ type: 'error', text1: 'Invalid connection', text2: reason });
            return;
        }
        setEdges(eds => addEdge(buildOptimisticEdge(connection), eds));

        const { branchID } = parseSourceHandleId(connection.sourceHandle);
        const sourceStep = steps.find(s => s.id === connection.source);
        savePathStepEdgeMutation.mutate({
            pathID: path?.id,
            sourceStepID: connection.source,
            targetStepID: connection.target,
            branchID,
            branchLabel: resolveBranchLabel({ sourceStep, branchID, defaultLabel: branchID === 'default' ? 'No Match' : null }),
            position: 0,
        });
    }, [nodes, edgesState, setEdges, steps, path?.id, savePathStepEdgeMutation]);

    const handleReconnectStart = useCallback((event, edge, _handleType) => {
        reconnectingEdgeID.current = edge.data?.pathEdgeID || null;
        reconnectSucceeded.current = false;
    }, []);

    const handleReconnect = useCallback((oldEdge, newConnection) => {
        const edgeID = oldEdge.data?.pathEdgeID;
        const { ok, reason } = validateConnection({
            connection: newConnection, nodes, edges: edgesState, ignoreEdgeID: edgeID,
        });
        if (!ok) {
            Toast.show({ type: 'error', text1: 'Invalid reconnection', text2: reason });
            return;
        }
        reconnectSucceeded.current = true;
        setEdges(eds => reconnectEdge(oldEdge, newConnection, eds, { shouldReplaceId: false }));

        const { branchID } = parseSourceHandleId(newConnection.sourceHandle);
        const sourceStep = steps.find(s => s.id === newConnection.source);
        savePathStepEdgeMutation.mutate({
            id: edgeID,
            pathID: path?.id,
            sourceStepID: newConnection.source,
            targetStepID: newConnection.target,
            branchID,
            branchLabel: resolveBranchLabel({ sourceStep, branchID, defaultLabel: branchID === 'default' ? 'No Match' : null }),
            position: 0,
        });
    }, [nodes, edgesState, setEdges, steps, path?.id, savePathStepEdgeMutation]);

    const handleReconnectEnd = useCallback(() => {
        reconnectingEdgeID.current = null;
        reconnectSucceeded.current = false;
    }, []);

    const handleConnectEnd = useCallback((event, connectionState) => {
        if (readOnly || connectionState.isValid) return;
        if (connectionState.fromHandle?.type !== 'source') return;
        if (reconnectingEdgeID.current) return;

        const fromNode = connectionState.fromNode;
        const fromHandleId = connectionState.fromHandle?.id;
        const slot = fromHandleId || DEFAULT_SOURCE_HANDLE_ID;
        const occupied = edgesState.find(e => e.type === 'pathEdge'
            && e.source === fromNode.id
            && (e.sourceHandle || DEFAULT_SOURCE_HANDLE_ID) === slot);
        if (occupied) {
            Toast.show({
                type: 'error',
                text1: 'Invalid connection',
                text2: 'That output already goes somewhere. Drag the existing connection instead.',
            });
            return;
        }

        const sourceStep = steps.find(s => s.id === fromNode.id);
        const { branchID } = parseSourceHandleId(fromHandleId);

        const e = 'changedTouches' in event ? event.changedTouches[0] : event;
        const canvasPosition = screenToFlowPosition({ x: e.clientX, y: e.clientY });

        onAddStep?.(
            fromNode.id,
            branchID,
            resolveBranchLabel({ sourceStep, branchID, defaultLabel: branchID === 'default' ? 'No Match' : null }),
            canvasPosition
        );
    }, [readOnly, edgesState, steps, screenToFlowPosition, onAddStep]);

    function handleDeleteEdgeFromData({ edgeID }) {
        deletePathStepEdgeMutation.mutate({ edgeID });
    }

    const handleEdgesDelete = useCallback((deleted) => {
        deleted.forEach(edge => {
            if (edge.type === 'pathEdge' && edge.data?.pathEdgeID) {
                const edgeData = dbEdges.find(e => e.id === edge.data.pathEdgeID);
                if (edgeData) {
                    lastDeletedEdgeRef.current = {
                        pathID: edgeData.pathID,
                        sourceStepID: edgeData.sourceStepID,
                        targetStepID: edgeData.targetStepID,
                        branchID: edgeData.branchID,
                        branchLabel: edgeData.branchLabel,
                        position: edgeData.position,
                    };
                }
                deletePathStepEdgeMutation.mutate({ edgeID: edge.data.pathEdgeID });
                Toast.show({
                    type: 'success',
                    text1: 'Connection removed',
                    text2: 'Tap to undo',
                    position: 'bottom',
                    onPress: () => {
                        const payload = lastDeletedEdgeRef.current;
                        if (payload) savePathStepEdgeMutation.mutate(payload);
                    },
                });
            }
        });
    }, [deletePathStepEdgeMutation, savePathStepEdgeMutation, dbEdges]);

    const handleBeforeDelete = useCallback(({ nodes: _toDeleteNodes, edges: toDeleteEdges }) => {
        const active = typeof document !== 'undefined' ? document.activeElement : null;
        if (active && (active.tagName === 'INPUT' || active.tagName === 'TEXTAREA' || active.contentEditable === 'true')) {
            return { nodes: [], edges: [] };
        }
        return { nodes: [], edges: readOnly ? [] : toDeleteEdges.filter(e => e.type === 'pathEdge') };
    }, [readOnly]);

    function handleStepMenu(step, action) {
        if (action === 'edit') {
            onEditStep?.(step);
            return;
        }
        if (action === 'duplicate') {
            // Find the leaf step (end of chain) — no outgoing pathEdge
            const sourceIDs = new Set(edgesState.filter(e => e.type === 'pathEdge').map(e => e.source));
            const nonTriggerSteps = steps.filter(s => s.stepType !== 'TRIGGER');
            const leafSteps = nonTriggerSteps.filter(s => !sourceIDs.has(s.id));
            let sourceStepID;
            let canvasPosition;
            if (leafSteps.length > 0) {
                const leafStep = leafSteps.sort((a, b) => (b.position || 0) - (a.position || 0))[0];
                sourceStepID = leafStep.id;
                const leafNode = getNode(leafStep.id);
                if (leafNode) {
                    canvasPosition = { x: leafNode.position.x, y: leafNode.position.y + getNodeHeight(leafNode) + LAYOUT_GAP_Y };
                }
            } else {
                const triggerStep = steps.find(s => s.stepType === 'TRIGGER');
                sourceStepID = triggerStep?.id;
            }
            onDuplicateStep?.(step, sourceStepID, canvasPosition);
            return;
        }
        if (action === 'insertAfter') {
            const node = getNode(step.id);
            // Place the new step directly below (same x) with the same vertical
            // gap the Tidy Up auto-layout uses.
            onAddStep?.(step.id, null, null, node ? { x: node.position.x, y: node.position.y + getNodeHeight(node) + LAYOUT_GAP_Y } : undefined);
            return;
        }
        if (action === 'detachStep') {
            detachPathStepMutation.mutate({ stepID: step.id });
            return;
        }

        if (action === 'moveUp') {
            const inEdges = edgesState.filter(e => e.type === 'pathEdge' && e.target === step.id);
            if (!inEdges.length) {
                Toast.show({ type: 'error', text1: 'Cannot move up', text2: 'This step has no predecessor.' });
                return;
            }
            const upperStepID = inEdges[0].source;
            if (steps.some(s => s.id === upperStepID && s.stepType === 'TRIGGER')) {
                Toast.show({ type: 'error', text1: 'Cannot move up', text2: 'The first step cannot move above the trigger.' });
                return;
            }
            swapMutation.mutate({ upperStepID, lowerStepID: step.id });
            return;
        }

        if (action === 'moveDown') {
            const outEdges = edgesState.filter(e => e.type === 'pathEdge' && e.source === step.id);
            if (!outEdges.length) {
                Toast.show({ type: 'error', text1: 'Cannot move down', text2: 'This step has no successor.' });
                return;
            }
            const lowerStepID = outEdges[0].target;
            swapMutation.mutate({ upperStepID: step.id, lowerStepID });
            return;
        }
    }

    // Keep ref in sync so buildGraph (which runs in an effect with limited deps)
    // always calls the latest handleStepMenu, even after hot-reload.
    handleStepMenuRef.current = handleStepMenu;

    const handleNodesChange = useCallback((changes) => {
        onNodesChange(changes);
        // Auto-save the layout as soon as a drag ends. Apply the final change
        // batch on top of the last committed nodes so the save uses the
        // post-drag positions (state/ref sync is async at this point).
        const endedDrag = changes.some(c => c.type === 'position' && c.dragging === false);
        if (endedDrag) {
            const next = applyNodeChanges(changes, nodesRef.current);
            flushPositions(next);
        }
    }, [onNodesChange]);

    const handleTidyUp = useCallback(() => {
        const positions = autoLayout(nodes, edgesState);
        const next = nodes.map(n => positions[n.id] ? { ...n, position: positions[n.id] } : n);
        setNodes(next);
        requestAnimationFrame(() => {
            fitView({ padding: 0.2, duration: 300 });
            flushPositions(next);
        });
    }, [nodes, edgesState, setNodes, fitView]);

    const handleNodeClick = useCallback((event, node) => {
        if (node.type === 'step' && onSelectStep) onSelectStep(node.data?.step || null);
    }, [onSelectStep]);

    const handlePaneClick = useCallback(() => {
        if (onSelectStep) onSelectStep(null);
    }, [onSelectStep]);

    const collectDescendantIds = useCallback((stepID) => {
        const set = new Set();
        const walk = (id) => {
            edgesState.filter(e => e.type === 'pathEdge' && e.source === id).forEach(e => {
                if (!set.has(e.target)) {
                    set.add(e.target);
                    walk(e.target);
                }
            });
        };
        walk(stepID);
        return set;
    }, [edgesState]);

    const handleNodeDrag = useCallback((_event, node) => {
        if (readOnly || node.type !== 'step') return;
        if (draggingStepRef.current?.stepID !== node.id) {
            draggingStepRef.current = {
                stepID: node.id,
                descendants: collectDescendantIds(node.id),
                lastPosition: node.position,
            };
        }
        const { descendants, lastPosition } = draggingStepRef.current;

        // Move all downstream (descendant) steps by the exact same delta.
        const dx = node.position.x - (lastPosition?.x ?? node.position.x);
        const dy = node.position.y - (lastPosition?.y ?? node.position.y);
        draggingStepRef.current.lastPosition = node.position;
        if ((dx !== 0 || dy !== 0) && descendants.size > 0) {
            setNodes(prev => prev.map(n =>
                descendants.has(n.id)
                    ? { ...n, position: { x: n.position.x + dx, y: n.position.y + dy } }
                    : n
            ));
        }

        const centerX = node.position.x + NODE_WIDTH / 2;
        const centerY = node.position.y + getNodeHeight(node) / 2;

        let best = null;
        let bestDist = Infinity;
        for (const edge of edgesState) {
            if (edge.type !== 'pathEdge' || !edge.data?.pathEdgeID) continue;
            if (edge.source === node.id || edge.target === node.id) continue;
            if (descendants.has(edge.source) || descendants.has(edge.target)) continue;
            const a = getNode(edge.source);
            const b = getNode(edge.target);
            if (!a || !b) continue;
            const mx = (a.position.x + NODE_WIDTH / 2 + b.position.x + NODE_WIDTH / 2) / 2;
            const my = (a.position.y + getNodeHeight(a) / 2 + b.position.y + getNodeHeight(b) / 2) / 2;
            const dist = Math.hypot(mx - centerX, my - centerY);
            if (dist < 80 && dist < bestDist) {
                bestDist = dist;
                best = edge;
            }
        }

        if (best) {
            const sourceName = steps.find(s => s.id === best.source)?.name || 'Start';
            const targetName = steps.find(s => s.id === best.target)?.name || 'End';
            setHoveredEdgeID(best.id);
            setSpliceHint({ sourceName, targetName });
        } else {
            setHoveredEdgeID(null);
            setSpliceHint(null);
        }
    }, [readOnly, edgesState, steps, getNode, setNodes, collectDescendantIds]);

    const handleNodeDragStop = useCallback((_event, node) => {
        if (readOnly || node.type !== 'step') return;
        draggingStepRef.current = null;
        if (!hoveredEdgeID) {
            setSpliceHint(null);
            return;
        }
        const edge = edgesState.find(e => e.id === hoveredEdgeID);
        setHoveredEdgeID(null);
        setSpliceHint(null);
        if (!edge) return;

        const ok = validateMove({
            edgeID: edge.data?.pathEdgeID,
            source: edge.source,
            target: edge.target,
            stepID: node.id,
            nodes,
            edges: edgesState,
        });
        if (!ok) {
            Toast.show({ type: 'error', text1: 'Cannot move here', text2: 'That would create a loop.' });
            return;
        }

        moveStepOntoEdgeMutation.mutate({ stepID: node.id, edgeID: edge.data?.pathEdgeID });
    }, [readOnly, edgesState, hoveredEdgeID, nodes, path?.id, moveStepOntoEdgeMutation]);

    return (
        <ReactFlow
            nodes={nodes}
            edges={displayEdges}
            nodeTypes={nodeTypes}
            edgeTypes={edgeTypes}
            onNodesChange={handleNodesChange}
            onEdgesChange={onEdgesChange}
            onNodeClick={handleNodeClick}
            onPaneClick={handlePaneClick}
            onNodeDrag={handleNodeDrag}
            onNodeDragStop={handleNodeDragStop}
            onConnect={handleConnect}
            onConnectEnd={handleConnectEnd}
            onReconnect={handleReconnect}
            onReconnectStart={handleReconnectStart}
            onReconnectEnd={handleReconnectEnd}
            onEdgesDelete={handleEdgesDelete}
            onBeforeDelete={handleBeforeDelete}
            isValidConnection={handleIsValidConnection}
            nodesConnectable={!readOnly}
            nodesDraggable={!readOnly}
            edgesReconnectable={!readOnly}
            edgesFocusable={!readOnly}
            elementsSelectable={!readOnly}
            reconnectRadius={20}
            connectionRadius={30}
            connectionLineType="smoothstep"
            deleteKeyCode={readOnly ? null : ['Backspace', 'Delete']}
            fitView
            fitViewOptions={{ padding: 0.3, maxZoom: 1.2 }}
            proOptions={{ hideAttribution: true }}
            defaultEdgeOptions={{ type: 'pathEdge', markerEnd: { type: MarkerType.ArrowClosed } }}
            style={{ background: theme.colors.background, fontFamily: FONT }}
        >
            <Background color={isDark ? theme.colors.textTertiary : '#cbd5e1'} gap={20} size={1} />
            <Controls showInteractive={false} />
            {!readOnly && (
                <MiniMap
                    nodeColor={(n) => {
                        if (n.type === 'trigger') return theme.colors.primary;
                        if (n.type === 'condition') return theme.colors.warning;
                        return theme.colors.textSecondary;
                    }}
                    maskColor={isDark ? '#00000060' : '#ffffff80'}
                    style={{ backgroundColor: theme.colors.surface }}
                />
            )}
            {!readOnly && (
                <Panel position="top-left">
                    <View style={{ flexDirection: 'column', gap: 8, alignItems: 'flex-start' }}>
                        <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 10 }}>
                            <button
                                type="button"
                                onClick={handleTidyUp}
                                data-testid="path-flow-tidy-up"
                                style={{
                                    background: theme.colors.primary,
                                    color: theme.colors.onPrimary,
                                    border: 'none',
                                    borderRadius: 8,
                                    padding: '6px 12px',
                                    fontSize: 12,
                                    fontWeight: 600,
                                    cursor: 'pointer',
                                }}
                            >
                                Tidy Up
                            </button>
                            <div
                                data-testid="path-flow-layout-saved"
                                style={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    alignItems: 'center',
                                    gap: 5,
                                    opacity: layoutSaved ? 1 : 0,
                                    transition: 'opacity 400ms ease',
                                    pointerEvents: 'none',
                                    color: theme.colors.success,
                                    fontSize: 12,
                                    fontWeight: 600,
                                    fontFamily: FONT,
                                }}
                            >
                                <Unicon name="checkmark-circle" size={15} colorVariant="success" />
                                Layout Saved
                            </div>
                        </div>
                        {invalidStepIDs.size > 0 && (
                            <div style={{
                                background: theme.colors.errorContainer,
                                color: theme.colors.error,
                                borderRadius: 8,
                                padding: '6px 12px',
                                fontSize: 12,
                                fontWeight: 600,
                                boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                                maxWidth: 260,
                            }}>
                                {invalidStepIDs.size} step{invalidStepIDs.size === 1 ? '' : 's'} with no incoming connection. Please fix the connection to save.
                            </div>
                        )}
                    </View>
                </Panel>
            )}
            {!readOnly && (
                <Panel position="top-right">
                    <div style={{
                        background: theme.colors.surface,
                        border: `1px solid ${theme.colors.border}`,
                        borderRadius: 8,
                        padding: '8px 12px',
                        fontSize: 11,
                        color: theme.colors.textSecondary,
                        maxWidth: 220,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                    }}>
                        Double-click a step to edit · Drag a connection end to rewire · Drag a step onto a line to move it · + on a line to insert · Drag from a dot to empty space to add
                    </div>
                </Panel>
            )}
            {spliceHint && (
                <Panel position="bottom-center">
                    <div style={{
                        background: theme.colors.warning,
                        color: theme.colors.onWarning,
                        borderRadius: 20,
                        padding: '6px 14px',
                        fontSize: 12,
                        fontWeight: 600,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                        marginBottom: 16,
                    }}>
                        Release to insert between &ldquo;{spliceHint.sourceName}&rdquo; and &ldquo;{spliceHint.targetName}&rdquo;
                    </div>
                </Panel>
            )}
        </ReactFlow>
    );
}

export default function PathFlowEditor({
    path, steps, edges, onEditStep, onAddStep, onUpdateTrigger,
    onEnrollPerson, onEnrollRole, enrolling, onInsertStep,
    onSelectStep, enrollmentStepStatus, enrollmentStepDetails, enrollee, readOnly, outOfScopeBranches,
    triggeringForms, onDuplicateStep,
    triggeringEvents,
}) {
    return (
        <div style={{ width: '100%', height: '100%' }}>
            <ReactFlowProvider>
                <FlowEditorInner
                    path={path}
                    steps={steps}
                    edges={edges}
                    onEditStep={onEditStep}
                    onAddStep={onAddStep}
                    onUpdateTrigger={onUpdateTrigger}
                    onEnrollPerson={onEnrollPerson}
                    onEnrollRole={onEnrollRole}
                    enrolling={enrolling}
                    onInsertStep={onInsertStep}
                    onSelectStep={onSelectStep}
                    enrollmentStepStatus={enrollmentStepStatus}
                    enrollmentStepDetails={enrollmentStepDetails}
                    enrollee={enrollee}
                    readOnly={readOnly}
                    outOfScopeBranches={outOfScopeBranches}
                    triggeringForms={triggeringForms}
                    onDuplicateStep={onDuplicateStep}
                    triggeringEvents={triggeringEvents}
                />
            </ReactFlowProvider>
        </div>
    );
}
